import {
  Container,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import {
  CircularProggressIndicator,
  ShopRegionHeadline,
} from "../components-atoms";
import { LoadMoreIndicator } from "../components-atoms/LoadMoreIndicator";
import { InfoCard } from "../components-molecules/InfoCard";
import { NoResultsFound } from "../components-molecules/NoResultsFound";
import { ITheme, theme } from "../constants/theme";
import { setCurrentPromotionCode } from "../redux/actions/promotionCodes";
import { bottomText } from "../utils/promo-code/bottomText";
import { groupHeaders } from "../utils/promo-code/groupHeaders";
import { UsedCodesBottom } from "../components-molecules/UsedCodes";
import { useTranslation } from 'react-i18next';

interface IPromotionCode {
  Id: string;
  Created: Date;
  Expires: Date;
  StartDate: Date;
  MinimumSpend: number | null;
  Code: string;
  Amount: number;
  Percentage: number;
  PromoCodeType: string;
  DiscountType: string;
  UseType: string;
  CreatedFor?: string;
  Active: boolean;
  IsReferralVoucher: boolean;
}
interface IProps {
  listKey: string;
  onEdit: () => void;
  sortType: string;
  query: string;
  filter: any;
  loadedCount: number;
  loadMore: () => void;
}

export const PromotionCodeList = ({
  listKey,
  onEdit,
  sortType,
  query,
  filter,
  loadedCount,
  loadMore,
}: IProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [codes, loading]: [any, boolean] = useSelector((state: any) => [
    state.PromotionCodesSymbolsReducer.data as any,
    state.PromotionCodesSymbolsReducer.loading,
  ]);
  const filtersApplied = () => {
    let applied = false;

    Object.keys(filter).forEach((x: any, index: number) => {
      if (x === "MinimumSpend" || x === "Status") {
        if (filter[x]) {
          applied = true;
        }
      } else if (filter[x].length > 0) {
        applied = true;
      }
    });
    return applied;
  };
  const [filterApplied, setFilterApplied] = useState(filtersApplied());

  useEffect(() => {
    const result = filtersApplied();
    setFilterApplied(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  if (!codes || (!codes?.Data?.length && loading)) {
    return <CircularProggressIndicator />;
  }

  if (codes?.length === 0) {
    return <NoResultsFound query={query} filterApplied={filterApplied} />;
  }

  const getStamp = (item: any) => {
    if (listKey === "UsedCodes") {
      return item.CodeType === "ReferralUsage" ? `Referral Code` : item.CodeType === "VoucherUsage" ? `Voucher` : `Promotional Code`;
    }
    return item.IsReferralVoucher ? `Referral Voucher` : `Promotion Code`;
  }

  return (
    <InfiniteScroll
      style={{ overflow: "hidden" }}
      dataLength={codes?.Data?.length}
      next={loadMore}
      hasMore={loadedCount < codes.Count}
      scrollThreshold={0.99}
      loader={<LoadMoreIndicator />}
    >
      <Container className={classes.component}>
        {codes?.Data && codes?.Data?.length === 0 ? (
          <NoResultsFound query={query} filterApplied={filterApplied} />
        ) : (
          codes?.Data.map((sortedItem: any, index) => (
            <div key={sortedItem.GroupName}>
              {sortedItem?.[listKey]?.length > 0 && (
                <ShopRegionHeadline
                  data-automation="number-of-codes"
                  headlineTypeSingular="Code"
                  headLineTypePlural="Codes"
                  data={{
                    header: groupHeaders[sortType](sortedItem.GroupName),
                    count: sortedItem[listKey].length,
                  }}
                />
              )}
              {sortedItem[listKey].map((item: IPromotionCode) => (
                <InfoCard
                  key={item.Id}
                  data={{
                    ...item,
                    stamp: getStamp(item),
                    topText: `${item.Code}`,
bottomText: listKey === "UsedCodes" ? <UsedCodesBottom item={item} /> : bottomText(item, t('currencySymbol')),
                  }}
                  secondGrid={8}
                  thirdGrid={4}
                  disabled={true}
                >
                  <Grid container justifyContent="flex-end" alignItems="center">
                    <Typography
                      data-automation="promotion-code"
                      variant="body2"
                      className={
                        item.Active ? classes.active : classes.archived
                      }
                      component="span"
                    >
                      {item.Active ? "Active" : "Archived"}
                    </Typography>
                    {listKey === "PromoCodes" && !item.IsReferralVoucher ?
                      <IconButton
                        style={{ width: 40, height: 40 }}
                        data-automation="edit-code-button"
                        onClick={() => {
                          dispatch(setCurrentPromotionCode(item));
                          onEdit();
                        }}
                      >
                        <EditOutlinedIcon
                          htmlColor={theme.palette.text.secondary}
                        />
                      </IconButton>
                      : null}
                  </Grid>
                </InfoCard>
              ))}
            </div>
          ))
        )}
      </Container>
    </InfiniteScroll>
  );
};
const useStyles = makeStyles((theme: ITheme) => ({
  component: {
    display: "flex",
    flexDirection: "column",
    paddingLeft: 0,
    paddingRight: 0,
    marginTop: 24,
  },
  noRecordsFound: {
    display: "flex",
    paddingLeft: "24px",
    flexDirection: "column",
  },
  active: {
    color: theme.palette.success.main,
    fontWeight: 700,
    paddingRight: 12,
  },
  archived: {
    color: theme.palette.error.main,
    fontWeight: 700,
    paddingRight: 12,
  },
}));
